import {
  Button,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
  useMediaQuery,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { surepassTheme } from '../../theme/surepass';
import type { Checkpoint } from '../../networkRequest/checkpointsService';

type CannotDeleteCheckpointDialogProps = {
  open: boolean;
  checkpoint: Checkpoint;
  onClose: () => void;
};

/**
 * Shown instead of the delete confirmation for a partner checkpoint, which only
 * SurePass administrators can delete. There's nothing to decide, so the one
 * button is a plain dismiss (Button page: Cancel / dismiss is text, inherit).
 */
const CannotDeleteCheckpointDialog = ({ open, checkpoint, onClose }: CannotDeleteCheckpointDialogProps) => {
  // Below sm the dialog fills the screen (Dialog page). `fullScreen` is a prop, so it needs the hook.
  const isMobile = useMediaQuery(surepassTheme.breakpoints.down('sm'));
  const titleId = `cannot-delete-checkpoint-${checkpoint.id}-title`;
  const descriptionId = `cannot-delete-checkpoint-${checkpoint.id}-description`;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      maxWidth="xs"
      fullWidth
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
    >
      {/* No × in the header: the footer's Close is the same control, and two buttons
          both named "Close" would be indistinguishable to a screen reader. Escape and
          the scrim still close it too. */}
      <CardHeader
        // Dialog page, header with an icon: the red warning is decoration (the title
        // already names the dialog), so it takes no label.
        avatar={<WarningAmberIcon color="error" />}
        title={`${checkpoint.name} Can't Be Deleted`}
        slotProps={{
          // The avatar drops the title to body2, so it's set back to h3 like every dialog title.
          title: { id: titleId, component: 'h2', variant: 'h3' },
          avatar: { sx: { mr: 1.5, display: 'flex' } },
        }}
      />

      <DialogContent sx={{ p: 2 }}>
        <Typography id={descriptionId}>
          {checkpoint.partner?.name ?? 'A partner'} runs {checkpoint.name}, so only a SurePass
          administrator can delete it. Contact your SurePass administrator for help.
        </Typography>
      </DialogContent>

      {/* Right-aligned; full width on mobile, like any dialog footer (Dialog page). */}
      <DialogActions sx={{ p: 2, gap: 2 }}>
        <Button color="inherit" onClick={onClose} sx={{ flex: { xs: 1, sm: 'initial' } }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CannotDeleteCheckpointDialog;
